import { TroveMappings, Decimal } from "@liquity/lib-base";
import { TokenData, AdjustedTokenData, Collateral, CoinMode } from "../../../Types";
import tokenData from "../../../TokenData";
import { tokenDataOrder } from "../../../TokenData";
import { addString, subtractString, format, adjustValue } from "../../../Utils/number";
import { useLiquity } from "../../../Hooks/LiquityContext";
import { BlockPolledLiquityStore, EthersLiquityWithStore } from "@liquity/lib-ethers";

type formValues = { [key: string]: any };

export const checkCollateral = (affectedCollateral: TokenData[], values: formValues) => {
  let message = "";
  let description = "";
  let changes = false;
  let collateralCount = 0;
  let collateralApproved = false;

  affectedCollateral.map(collateral => {
    const { token, walletBalance, troveBalance } = collateral;
    const mode = values[token + "mode"];
    const change = parseFloat(values[token]);

    // Make sure some changes have been made
    if (change !== 0) {
      changes = true;
    }
    if (mode === "withdraw" && change > troveBalance) {
      message = `${token}: Invalid Withdraw`;
      description = "You cannot withdraw more than your current deposit.";
    } else if (mode === "deposit" && change > walletBalance) {
      message = `${token}: Invalid Deposit`;
      description = `You cannot deposit more than your current ${token} balance.`;
    } else {
      collateralCount += 1;
    }
    return token;
  });

  if (collateralCount === affectedCollateral.length) {
    collateralApproved = true;
  }

  return [changes, collateralApproved, message, description];
};

export const checkBorrow = (
  values: formValues,
  totalYUSDFromLever: Decimal,
  debt: Decimal,
  ICR: number
) => {
  let newTotalBorrow =
    values["YUSDmode"] === "deposit"
      ? addString(format(debt), values["YUSD"])
      : subtractString(format(debt), values["YUSD"]);
  // todo check if yusd from lever deposit or withdraw
  newTotalBorrow = newTotalBorrow + Number(totalYUSDFromLever.toString());
  const minBorrow = 1800;
  // TODO: The "Debt" passed in should actually also include the sum of all fees
  // which are going to be paid, on the collateral, and also depending on the amount borrowed the fee for
  // that as well.
  // This is how we do it in the solidity tests:
  // static async getNetBorrowingAmount(contracts, debtWithFee) {
  //   const borrowingRate = await contracts.troveManager.getBorrowingRateWithDecay()

  //   // console.log("Numerator", (this.toBN(debtWithFee).mul(MoneyValues._1e18BN)).toString());
  //   let value = this.toBN(debtWithFee).mul(MoneyValues._1e18BN).div(MoneyValues._1e18BN.add(borrowingRate))
  //   if (value.eq(this.toBN("1791044776119402985074")) ) {
  //     value = this.toBN("1791044776119402985075") // Off by one in min case
  //   }
  //   return value
  // }
  // Realistically it is like 1791.something with the fee of 0.5%.
  let message = "";
  let description = "";
  let borrowApproved = false;
  if (values["YUSDmode"] === "deposit" && newTotalBorrow < minBorrow) {
    message = "YUSD: Invalid Borrow";
    description = `You must borrow at least ${2000} YUSD`;
  } else if (ICR < 110) {
    message = "YUSD: Invalid Borrow";
    description = `Your new total YUSD borrow would make your collateral ratio ${ICR}%, which is below the minimum threshold of 110%.`;
  } else {
    borrowApproved = true;
  }

  return [borrowApproved, message, description];
};

export const getAffectedCollateral = (values: formValues): AdjustedTokenData[] => {
  let tokens: TokenData[] = [];
  Object.keys(values).map(key => {
    if (key.includes("mode") && key.slice(0, -4) != "YUSD") {
      tokens.push(tokenData[tokenDataOrder[key.slice(0, -4)]]);
    }
  });
  if (tokens.length == 0) {
    return JSON.parse(JSON.stringify(tokenData));
  }
  // console.log('values', values)
  const tokenDataChanges: AdjustedTokenData[] = JSON.parse(JSON.stringify(tokens));
  tokenDataChanges.map(token => {
    const change = values[token.token] ? parseFloat(String(values[token.token])) : 0;
    token["mode"] =
      values[token.token + "mode"] != undefined ? values[token.token + "mode"] : "deposit";
    token["change"] = change;
    token["yusdFromLever"] = values[token.token + "ExpectedYUSD"]
      ? values[token.token + "ExpectedYUSD"]
      : "0";
    token["changeFromLever"] = values[token.token + "PostLeverage"]
      ? values[token.token + "PostLeverage"]
      : "0";
  });
  //console.log("utils", tokenDataChanges)
  return tokenDataChanges;
};
export const getTempAffectedCollateral = (values: formValues): AdjustedTokenData[] => {
  // console.log("utils", values)
  const tokenDataChanges: AdjustedTokenData[] = JSON.parse(JSON.stringify(tokenData));
  tokenDataChanges.map(token => {
    // console.log(values[token.token.concat("tempExpectedYUSD")])
    const change = values[token.token] ? parseFloat(String(values[token.token])) : 0;
    token["mode"] = values[token.token] ? String(values[token.token + "mode"]) : "deposit";
    token["change"] = change;
    token["yusdFromLever"] = values[token.token + "tempExpectedYUSD"]
      ? values[token.token + "tempExpectedYUSD"]
      : "0";
    token["changeFromLever"] = values[token.token + "tempPostLeverage"]
      ? values[token.token + "tempPostLeverage"]
      : "0";
  });
  // console.log("utils", tokenDataChanges)
  return tokenDataChanges;
};
export const calculateVcValue = (
  affectedCollateral: AdjustedTokenData[],
  price: TroveMappings,
  values: any
) => {
  let totalVirtualCoinValue = 0;
  affectedCollateral.map(collateral => {
    const { address, safetyRatio, troveBalance, change, mode, changeFromLever, token } = collateral;
    let newTotal;
    if (changeFromLever !== "0") {
      if (values != undefined && values[token + "leverage"] != undefined) {
        newTotal = troveBalance + change * values[token + "leverage"];
      } else {
        newTotal = troveBalance + Number(changeFromLever);
      }
    } else {
      newTotal =
        mode === "deposit"
          ? troveBalance + change
          : troveBalance - change >= 0
          ? troveBalance - change
          : troveBalance;
    }
    totalVirtualCoinValue += safetyRatio * format(price[address]) * newTotal;
  });
  return totalVirtualCoinValue;
};

export const calculateTotalYUSDFromLever = (
  affectedCollateral: AdjustedTokenData[],
  prices: TroveMappings,
  values: any
): Decimal => {
  let totalYUSDFromLever: Decimal = Decimal.ZERO;
  // console.log('affectedCollateral', affectedCollateral)
  if (affectedCollateral[0].mode === "withdraw") {
    // console.log('hit 2')
    affectedCollateral.map(collateral => {
      const { yusdFromLever } = collateral;
      if (yusdFromLever != undefined && +yusdFromLever != 0) {
        collateral["yusdFromLever"] = String(
          Decimal.from(collateral.change).mul(prices[collateral.address])
        );
        totalYUSDFromLever = totalYUSDFromLever.add(Decimal.from(collateral["yusdFromLever"]));
      }
      return totalYUSDFromLever;
    });
  } else {
    // console.log('hit 3')
    affectedCollateral.map(collateral => {
      const { yusdFromLever } = collateral;
      // console.log('yusd inside', yusdFromLever)
      if (yusdFromLever != undefined && +yusdFromLever != 0) {
        if (!isNaN(values[collateral.token + "leverage"])) {
          collateral["yusdFromLever"] = String(
            Decimal.from(collateral.change)
              .mul(Decimal.from(values[collateral.token + "leverage"]))
              .mul(prices[collateral.address])
              .mul(Decimal.from(collateral.safetyRatio))
              .div(
                Decimal.from(values[collateral.token + "leverage"]).div(
                  Decimal.from(values[collateral.token + "leverage"]).sub(Decimal.ONE)
                )
              )
          );
        } else if (!isNaN(values[collateral.token.concat("templeverage")])) {
          const tempLeverage = values[collateral.token.concat("templeverage")];
          // console.log("temp", tempLeverage)
          collateral["yusdFromLever"] = String(
            Decimal.from(collateral.change)
              .mul(Decimal.from(tempLeverage))
              .mul(prices[collateral.address])
              .mul(Decimal.from(collateral.safetyRatio))
              .div(Decimal.from(tempLeverage).div(Decimal.from(tempLeverage).sub(Decimal.ONE)))
          );
        }

        totalYUSDFromLever = totalYUSDFromLever.add(Decimal.from(collateral["yusdFromLever"]));
      }
    });
  }
  // console.log('totalYUSDFromLever', totalYUSDFromLever)
  return totalYUSDFromLever;
};

export const getTroveValueWithLever = (
  mode: "deposit" | "withdraw",
  item: Collateral,
  values: any,
  borrowMode: "lever" | "unlever" | "normal"
): number => {
  if (borrowMode === "normal" || borrowMode === "unlever") {
    return adjustValue(mode, item.troveBalance, values[item.token]);
  } else {
    if (isNaN(values[item.token + "slippage"])) {
      return adjustValue(mode, item.troveBalance, values[item.token]);
    } else {
      return adjustValue(
        mode,
        item.troveBalance,
        String(+values[item.token] * values[item.token + "leverage"])
      );
    }
  }
};

export const getFeesCollateral = (
  affectedCollateral: AdjustedTokenData[],
  price: TroveMappings,
  fees: TroveMappings,
  values: any
) => {
  let totalFee = 0;
  // console.log('adjusted collat in fees', affectedCollateral)
  affectedCollateral.map(collateral => {
    const { address, safetyRatio, troveBalance, change, mode } = collateral;
    if (mode === "withdraw") {
      return 0;
    }
    const fee = fees[address] === undefined ? 0 : parseFloat(fees[address].toString());

    let changeCheck = 0;
    if (change != undefined) {
      changeCheck = !isNaN(values[collateral.token + "leverage"])
        ? change * values[collateral.token + "leverage"]
        : change;
    }
    // const fee = Math.min(0.01, fees[address] === undefined ? 0 : parseFloat(fees[address].toString()));
    totalFee += safetyRatio * format(price[address]) * changeCheck * fee;
  });
  return totalFee;
};

export const dec = (val: string, scale: any) => {
  let zerosCount = 0;

  if (scale == "ether") {
    zerosCount = 18;
  } else if (scale == "finney") zerosCount = 15;
  else {
    zerosCount = scale;
  }

  const strVal = val.toString();
  const strZeros = "0".repeat(zerosCount);

  return strVal.concat(strZeros);
};
